import React, { useState, useEffect } from 'react';
import styles from './StatsView.module.css';
import { API_BASE_URL, apiRequest } from '../../config/api';

const StatsView = () => {
  const [sessionStats, setSessionStats] = useState(null);
  const [activeMode, setActiveMode] = useState('correction');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedApp, setSelectedApp] = useState(localStorage.getItem('selectedApp') || 'grammar');

  useEffect(() => {
    fetchSessionStats();
  }, []);

  const fetchSessionStats = async () => {
    try {
      setLoading(true);
      const stats = await apiRequest('/sessions/stats');
      setSessionStats(stats);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderTimedStats = (timedStats) => {
    if (!timedStats) return null;
    
    return (
      <div className={styles.timedStats}>
        <p>Speed/Accuracy Balance: {timedStats.speedAccuracyBalance || 'N/A'}%</p>
      </div>
    );
  };

  const renderCorrectionStats = (stats) => (
    <div className={styles.modeStats}>
      <h3>Correction Mode Sessions</h3>
      <div className={styles.sessionsList}>
        {stats.map((session, index) => (
          <div key={index} className={styles.sessionCard}>
            <div className={styles.sessionHeader}>
              <h4>Session {stats.length - index}</h4>
              <span>{new Date(session.date).toLocaleDateString()}</span>
            </div>
            <div className={styles.sessionDetails}>
              {renderTimedStats(session.timedStats)}
              <p>Accuracy: {session.accuracy?.toFixed(2)}%</p>
              {/* <p>Time Spent: {session.totalTime} minutes</p> */}
              <div className={styles.errorSection}>
                <h5>Common Errors:</h5>
                <ul>
                  {session.commonErrors?.map((error, i) => (
                    <li key={i}>{error.type}: {error.count} times</li>
                  ))}
                </ul>
              </div>
              <div className={styles.suggestedTopics}>
                <h5>Suggested Topics to Practice:</h5>
                <ul>
                  {session.suggestedTopics?.map((topic, i) => (
                    <li key={i}>{topic}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderConstructionStats = (stats) => (
    <div className={styles.modeStats}>
      <h3>Construction Mode Sessions</h3>
      <div className={styles.sessionsList}>
        {stats.map((session, index) => (
          <div key={index} className={styles.sessionCard}>
            <div className={styles.sessionHeader}>
              <h4>Session {stats.length - index}</h4>
              <span>{new Date(session.date).toLocaleDateString()}</span>
            </div>
            <div className={styles.sessionDetails}>
              {renderTimedStats(session.timedStats)}
              <p>Accuracy: {session.wordOrderAccuracy?.toFixed(2)}%</p>
              <p>Average Time per Sentence: {session.averageTimePerSentence?.toFixed(2)}s</p>
              <div className={styles.errorSection}>
                <h5>Common Mistakes:</h5>
                <ul>
                  {session.commonMistakes?.map((mistake, i) => (
                    <li key={i}>{mistake.type}: {mistake.count} times</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAdaptiveStats = (stats) => (
    <div className={styles.modeStats}>
      <h3>Adaptive Learning Sessions</h3>
      <div className={styles.sessionsList}>
        {stats.map((session, index) => (
          <div key={index} className={styles.sessionCard}>
            <div className={styles.sessionHeader}>
              <h4>Session {stats.length - index}</h4>
              <span>{new Date(session.date).toLocaleDateString()}</span>
            </div>
            <div className={styles.sessionDetails}>
              {renderTimedStats(session.timedStats)}
              <p>Improvement Rate: {session.improvementRate}%</p>
              <p>Current Level: {session.difficultyProgression?.slice(-1)[0]?.level}</p>
              <div className={styles.topicSection}>
                <h5>Topic Performance:</h5>
                <ul>
                  {session.topicAccuracy?.map((topic, i) => (
                    <li key={i}>
                      {topic.topic}: {(topic.correct / topic.total * 100).toFixed(1)}%
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStoryStats = (stats) => (
    <div className={styles.modeStats}>
      <h3>Story Mode Sessions</h3>
      <div className={styles.sessionsList}>
        {stats.map((session, index) => (
          <div key={index} className={styles.sessionCard}>
            <div className={styles.sessionHeader}>
              <h4>Session {stats.length - index}</h4>
              <span>{new Date(session.date).toLocaleDateString()}</span>
            </div>
            <div className={styles.sessionDetails}>
              {renderTimedStats(session.timedStats)}
              <p>Completion Rate: {session.completionRate}%</p>
              {/* <p>Total Time: {session.totalTime} minutes</p> */}
              <div className={styles.grammarSection}>
                <h5>Grammar Choices:</h5>
                <ul>
                  {session.grammarChoiceAccuracy?.map((choice, i) => (
                    <li key={i}>
                      {choice.type}: {(choice.correct / choice.total * 100).toFixed(1)}%
                    </li>
                  ))}
                </ul>
              </div>
              <div className={styles.struggleSection}>
                <h5>Struggle Patterns:</h5>
                <ul>
                  {session.strugglePatterns?.map((pattern, i) => (
                    <li key={i}>
                      {pattern.pattern}: {pattern.frequency} time(s)
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderExamStats = (stats) => (
    <div className={styles.modeStats}>
      <h3>Exam Mode Sessions</h3>
      <div className={styles.sessionsList}>
        {stats.map((session, index) => (
          <div key={index} className={styles.sessionCard}>
            <div className={styles.sessionHeader}>
              <h4>Session {stats.length - index}</h4>
              <span>{new Date(session.date).toLocaleDateString()}</span>
            </div>
            <div className={styles.sessionDetails}>
              {renderTimedStats(session.timedStats)}
              <p>Overall Score: {session.overallScore?.toFixed(2)}%</p>
              <p>Average Time per Question: {session.timeManagement?.averageTimePerQuestion?.toFixed(2)}s</p>
              <p>Timed Out Questions: {session.timeManagement?.timeoutQuestions}</p>
              <div className={styles.topicSection}>
                <h5>Topic Breakdown:</h5>
                <ul>
                  {session.topicBreakdown?.map((topic, i) => (
                    <li key={i}>
                      {topic.topic}: {(topic.correct / topic.total * 100).toFixed(1)}%
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStoryExamStats = (stats) => (
    <div className={styles.modeStats}>
      <h3>Story Exam Sessions</h3>
      <div className={styles.sessionsList}>
        {stats.map((session, index) => (
          <div key={index} className={styles.sessionCard}>
            <div className={styles.sessionHeader}>
              <h4>Session {stats.length - index}</h4>
              <span>{new Date(session.date).toLocaleDateString()}</span>
            </div>
            <div className={styles.sessionDetails}>
              {renderTimedStats(session.timedStats)}
              <p>Overall Score: {session.overallScore}%</p>
              <p>Comprehension Score: {session.comprehensionScore}%</p>
              <p>Grammar Score: {session.grammarScore}%</p>
              <p>Reading Time: {session.readingTime} minutes</p>
              <div className={styles.topicSection}>
                <h5>Topic Performance:</h5>
                <ul>
                  {session.topicBreakdown?.map((topic, i) => (
                    <li key={i}>
                      {topic.topic}: {(topic.correct / topic.total * 100).toFixed(1)}%
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // New rendering functions for spelling modes
  const renderWordCompletionStats = (stats) => (
    <div className={styles.modeStats}>
      <h3>Word Completion Sessions</h3>
      <div className={styles.sessionsList}>
        {stats.map((session, index) => (
          <div key={index} className={styles.sessionCard}>
            <div className={styles.sessionHeader}>
              <h4>Session {stats.length - index}</h4>
              <span>{new Date(session.date).toLocaleDateString()}</span>
            </div>
            <div className={styles.sessionDetails}>
              <p>Accuracy: {session.accuracy?.toFixed(2)}%</p>
              <p>Average Response Time: {session.averageResponseTime?.toFixed(2)}s</p>
              <div className={styles.errorSection}>
                <h5>Error Patterns:</h5>
                <ul>
                  {session.errorPatterns?.map((pattern, i) => (
                    <li key={i}>{pattern.pattern}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAudioSpellingStats = (stats) => (
    <div className={styles.modeStats}>
      <h3>Audio Spelling Sessions</h3>
      <div className={styles.sessionsList}>
        {stats.map((session, index) => (
          <div key={index} className={styles.sessionCard}>
            <div className={styles.sessionHeader}>
              <h4>Session {stats.length - index}</h4>
              <span>{new Date(session.date).toLocaleDateString()}</span>
            </div>
            <div className={styles.sessionDetails}>
              <p>Accuracy: {session.accuracy?.toFixed(2)}%</p>
              <p>Average Response Time: {session.averageResponseTime?.toFixed(2)}s</p>
              <div className={styles.errorSection}>
                <h5>Error Patterns:</h5>
                <ul>
                  {session.errorPatterns?.map((pattern, i) => (
                    <li key={i}>{pattern.pattern}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderTypingChallengeStats = (stats) => (
    <div className={styles.modeStats}>
      <h3>Typing Challenge Sessions</h3>
      <div className={styles.sessionsList}>
        {stats.map((session, index) => (
          <div key={index} className={styles.sessionCard}>
            <div className={styles.sessionHeader}>
              <h4>Session {stats.length - index}</h4>
              <span>{new Date(session.date).toLocaleDateString()}</span>
            </div>
            <div className={styles.sessionDetails}>
              <p>Accuracy: {session.accuracy?.toFixed(2)}%</p>
              <p>Average Response Time: {session.averageResponseTime?.toFixed(2)}s</p>
              <div className={styles.errorSection}>
                <h5>Error Patterns:</h5>
                <ul>
                  {session.errorPatterns?.map((pattern, i) => (
                    <li key={i}>{pattern.pattern}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAnagramChallengeStats = (stats) => (
    <div className={styles.modeStats}>
      <h3>Anagram Challenge Sessions</h3>
      <div className={styles.sessionsList}>
        {stats.map((session, index) => (
          <div key={index} className={styles.sessionCard}>
            <div className={styles.sessionHeader}>
              <h4>Session {stats.length - index}</h4>
              <span>{new Date(session.date).toLocaleDateString()}</span>
            </div>
            <div className={styles.sessionDetails}>
              <p>Accuracy: {session.accuracy?.toFixed(2)}%</p>
              <p>Average Response Time: {session.averageResponseTime?.toFixed(2)}s</p>
              <div className={styles.errorSection}>
                <h5>Error Patterns:</h5>
                <ul>
                  {session.errorPatterns?.map((pattern, i) => (
                    <li key={i}>{pattern.pattern}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderDefinitionSpellingStats = (stats) => (
    <div className={styles.modeStats}>
      <h3>Definition Spelling Sessions</h3>
      <div className={styles.sessionsList}>
        {stats.map((session, index) => (
          <div key={index} className={styles.sessionCard}>
            <div className={styles.sessionHeader}>
              <h4>Session {stats.length - index}</h4>
              <span>{new Date(session.date).toLocaleDateString()}</span>
            </div>
            <div className={styles.sessionDetails}>
              <p>Accuracy: {session.accuracy?.toFixed(2)}%</p>
              <p>Average Response Time: {session.averageResponseTime?.toFixed(2)}s</p>
              <div className={styles.errorSection}>
                <h5>Error Patterns:</h5>
                <ul>
                  {session.errorPatterns?.map((pattern, i) => (
                    <li key={i}>{pattern.pattern}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderSpellingBeeStats = (stats) => (
    <div className={styles.modeStats}>
      <h3>Spelling Bee Sessions</h3>
      <div className={styles.sessionsList}>
        {stats.map((session, index) => (
          <div key={index} className={styles.sessionCard}>
            <div className={styles.sessionHeader}>
              <h4>Session {stats.length - index}</h4>
              <span>{new Date(session.date).toLocaleDateString()}</span>
            </div>
            <div className={styles.sessionDetails}>
              <p>Accuracy: {session.accuracy?.toFixed(2)}%</p>
              <p>Average Response Time: {session.averageResponseTime?.toFixed(2)}s</p>
              <div className={styles.errorSection}>
                <h5>Error Patterns:</h5>
                <ul>
                  {session.errorPatterns?.map((pattern, i) => (
                    <li key={i}>{pattern.pattern}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStorySpellingStats = (stats) => (
    <div className={styles.modeStats}>
      <h3>Story Spelling Sessions</h3>
      <div className={styles.sessionsList}>
        {stats.map((session, index) => (
          <div key={index} className={styles.sessionCard}>
            <div className={styles.sessionHeader}>
              <h4>Session {stats.length - index}</h4>
              <span>{new Date(session.date).toLocaleDateString()}</span>
            </div>
            <div className={styles.sessionDetails}>
              <p>Accuracy: {session.accuracy?.toFixed(2)}%</p>
              <p>Average Response Time: {session.averageResponseTime?.toFixed(2)}s</p>
              <div className={styles.errorSection}>
                <h5>Error Patterns:</h5>
                <ul>
                  {session.errorPatterns?.map((pattern, i) => (
                    <li key={i}>{pattern.pattern}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Update the renderModeStats function to include spelling modes
  const renderModeStats = (mode) => {
    if (!sessionStats?.[mode] || sessionStats[mode].length === 0) {
      return <div className={styles.noData}>No data available for this mode yet.</div>;
    }

    const stats = sessionStats[mode];
    
    switch (mode) {
      // Grammar modes
      case 'correction':
        return renderCorrectionStats(stats);
      case 'construction':
        return renderConstructionStats(stats);
      case 'adaptive':
        return renderAdaptiveStats(stats);
      case 'story':
        return renderStoryStats(stats);
      case 'exam':
        return renderExamStats(stats);
      case 'storyExam':
        return renderStoryExamStats(stats);
      
      // Spelling modes
      case 'word_completion':
        return renderWordCompletionStats(stats);
      case 'audio_spelling':
        return renderAudioSpellingStats(stats);
      case 'typing_challenge':
        return renderTypingChallengeStats(stats);
      case 'anagram_challenge':
        return renderAnagramChallengeStats(stats);
      case 'definition_spelling':
        return renderDefinitionSpellingStats(stats);
      case 'spelling_bee':
        return renderSpellingBeeStats(stats);
      case 'story_spelling':
        return renderStorySpellingStats(stats);
      default:
        return null;
    }
  };

  // Render grammar or spelling mode tabs based on selected app
  const renderModeTabs = () => {
    if (selectedApp === 'grammar') {
      return (
        <div className={styles.modesTabs}>
          <button 
            className={`${styles.modeTab} ${activeMode === 'correction' ? styles.active : ''}`}
            onClick={() => setActiveMode('correction')}
          >
            Correction Mode
          </button>
          <button 
            className={`${styles.modeTab} ${activeMode === 'construction' ? styles.active : ''}`}
            onClick={() => setActiveMode('construction')}
          >
            Construction Mode
          </button>
          <button 
            className={`${styles.modeTab} ${activeMode === 'adaptive' ? styles.active : ''}`}
            onClick={() => setActiveMode('adaptive')}
          >
            Adaptive Mode
          </button>
          <button 
            className={`${styles.modeTab} ${activeMode === 'story' ? styles.active : ''}`}
            onClick={() => setActiveMode('story')}
          >
            Story Mode
          </button>
          <button 
            className={`${styles.modeTab} ${activeMode === 'exam' ? styles.active : ''}`}
            onClick={() => setActiveMode('exam')}
          >
            Exam Mode
          </button>
          <button 
            className={`${styles.modeTab} ${activeMode === 'storyExam' ? styles.active : ''}`}
            onClick={() => setActiveMode('storyExam')}
          >
            Story Exam
          </button>
        </div>
      );
    } else {
      return (
        <div className={styles.modesTabs}>
          <button 
            className={`${styles.modeTab} ${activeMode === 'word_completion' ? styles.active : ''}`}
            onClick={() => setActiveMode('word_completion')}
          >
            Word Completion
          </button>
          <button 
            className={`${styles.modeTab} ${activeMode === 'audio_spelling' ? styles.active : ''}`}
            onClick={() => setActiveMode('audio_spelling')}
          >
            Audio Spelling
          </button>
          <button 
            className={`${styles.modeTab} ${activeMode === 'typing_challenge' ? styles.active : ''}`}
            onClick={() => setActiveMode('typing_challenge')}
          >
            Typing Challenge
          </button>
          <button 
            className={`${styles.modeTab} ${activeMode === 'anagram_challenge' ? styles.active : ''}`}
            onClick={() => setActiveMode('anagram_challenge')}
          >
            Anagram Challenge
          </button>
          <button 
            className={`${styles.modeTab} ${activeMode === 'definition_spelling' ? styles.active : ''}`}
            onClick={() => setActiveMode('definition_spelling')}
          >
            Definition Spelling
          </button>
          <button 
            className={`${styles.modeTab} ${activeMode === 'spelling_bee' ? styles.active : ''}`}
            onClick={() => setActiveMode('spelling_bee')}
          >
            Spelling Bee
          </button>
          <button 
            className={`${styles.modeTab} ${activeMode === 'story_spelling' ? styles.active : ''}`}
            onClick={() => setActiveMode('story_spelling')}
          >
            Story Spelling
          </button>
        </div>
      );
    }
  };

  // Update useEffect to set initial active mode based on selected app
  useEffect(() => {
    if (selectedApp === 'grammar') {
      setActiveMode('correction');
    } else {
      setActiveMode('word_completion');
    }
  }, [selectedApp]);

  if (loading) {
    return <div className={styles.loading}>Loading stats...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  return (
    <div className={styles.container}>
      {renderModeTabs()}
      {renderModeStats(activeMode)}
    </div>
  );
};

export default StatsView; 